import type { EmailIdentification, MemberManagementService, MemberManagementViewModel } from './appService';
import { readCloudApiErrorMessage } from './cloudApi';
import type { UserAccount } from './model';

export const CLOUD_AUTH_SLICE_UNSUPPORTED_OPERATION_MESSAGE =
  'Cloud auth slice only supports account authentication in this staging mode';

export interface CloudAuthSliceTokenStore {
  readonly get: () => string | null;
  readonly set: (token: string) => void;
  readonly clear: () => void;
}

export interface CloudAuthSliceServiceConfig {
  readonly apiBaseUrl: string;
  readonly fetch: (url: string, init?: RequestInit) => Promise<Response>;
  readonly tokenStore: CloudAuthSliceTokenStore;
}

interface AuthSessionResponse {
  readonly sessionToken: string;
  readonly user: UserAccount;
}

interface CurrentUserResponse {
  readonly user: UserAccount;
}

export function createCloudAuthSliceService(config: CloudAuthSliceServiceConfig): MemberManagementService {
  const apiBaseUrl = config.apiBaseUrl.replace(/\/+$/, '');

  const request = async <T>(path: string, init: RequestInit & { readonly requireAuth?: boolean } = {}): Promise<T> => {
    const { requireAuth, headers: initHeaders, ...fetchInit } = init;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(initHeaders as Record<string, string> | undefined)
    };
    const token = config.tokenStore.get();

    if (requireAuth) {
      if (!token) {
        throw new Error('Cloud auth slice requires a session token');
      }

      headers.Authorization = `Bearer ${token}`;
    }

    const response = await config.fetch(`${apiBaseUrl}${path}`, {
      ...fetchInit,
      headers
    });
    const body = (await response.json()) as unknown;

    if (!response.ok) {
      throw new Error(readCloudApiErrorMessage(body, 'Cloud API request failed'));
    }

    return body as T;
  };

  const saveSession = (response: AuthSessionResponse): void => {
    config.tokenStore.set(response.sessionToken);
  };

  return {
    identifyEmail: async (input): Promise<EmailIdentification> =>
      await request<EmailIdentification>('/api/auth/identify-email', {
        method: 'POST',
        body: JSON.stringify(input)
      }),

    register: async (input) => {
      saveSession(
        await request<AuthSessionResponse>('/api/auth/register', {
          method: 'POST',
          body: JSON.stringify(input)
        })
      );
    },

    login: async (input) => {
      saveSession(
        await request<AuthSessionResponse>('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify(input)
        })
      );
    },

    logout: async () => {
      if (config.tokenStore.get()) {
        await request<{ readonly status: 'ok' }>('/api/auth/logout', {
          method: 'POST',
          requireAuth: true
        });
      }

      config.tokenStore.clear();
    },

    getViewModel: async () => {
      const token = config.tokenStore.get();

      if (!token) {
        return createEmptyAuthSliceViewModel(null);
      }

      const response = await request<CurrentUserResponse>('/api/me', {
        method: 'GET',
        requireAuth: true
      });

      return createEmptyAuthSliceViewModel(response.user);
    },

    createChoir: unsupported('createChoir'),
    switchChoir: unsupported('switchChoir'),
    listMemberDisplayItems: unsupported('listMemberDisplayItems'),
    listMemberManagementItems: unsupported('listMemberManagementItems'),
    listMemberSelectorItems: unsupported('listMemberSelectorItems'),
    listMemberGroupSummaries: unsupported('listMemberGroupSummaries'),
    listPermissionGroups: unsupported('listPermissionGroups'),
    getPermissionGroupDetail: unsupported('getPermissionGroupDetail'),
    createPermissionGroup: unsupported('createPermissionGroup'),
    updatePermissionGroup: unsupported('updatePermissionGroup'),
    deletePermissionGroup: unsupported('deletePermissionGroup'),
    addPermissionGroupMember: unsupported('addPermissionGroupMember'),
    removePermissionGroupMember: unsupported('removePermissionGroupMember'),
    listInvitations: unsupported('listInvitations'),
    listApprovals: unsupported('listApprovals'),
    getApprovalDetail: unsupported('getApprovalDetail'),
    generateInvitationCode: unsupported('generateInvitationCode'),
    previewInvitationCode: unsupported('previewInvitationCode'),
    leaveCurrentChoirWorkspace: unsupported('leaveCurrentChoirWorkspace'),
    deleteInvitationCode: unsupported('deleteInvitationCode'),
    submitJoinRequest: unsupported('submitJoinRequest'),
    approveJoinRequest: unsupported('approveJoinRequest'),
    rejectJoinRequest: unsupported('rejectJoinRequest'),
    createMemberGroup: unsupported('createMemberGroup'),
    renameMemberGroup: unsupported('renameMemberGroup'),
    deleteMemberGroup: unsupported('deleteMemberGroup'),
    addMemberGroupMember: unsupported('addMemberGroupMember'),
    removeMemberGroupMember: unsupported('removeMemberGroupMember'),
    setMemberGroupManagerGrant: unsupported('setMemberGroupManagerGrant'),
    createMemberGroupTag: unsupported('createMemberGroupTag'),
    renameMemberGroupTag: unsupported('renameMemberGroupTag'),
    deleteMemberGroupTag: unsupported('deleteMemberGroupTag'),
    setMemberGroupTagMembership: unsupported('setMemberGroupTagMembership'),
    getMemberGroupDetail: unsupported('getMemberGroupDetail'),
    listSchedules: unsupported('listSchedules'),
    listUpcomingSchedules: unsupported('listUpcomingSchedules'),
    getScheduleDetail: unsupported('getScheduleDetail'),
    getHomeUpcomingSchedule: unsupported('getHomeUpcomingSchedule'),
    getScheduleEditOptions: unsupported('getScheduleEditOptions'),
    createSchedule: unsupported('createSchedule'),
    updateSchedule: unsupported('updateSchedule'),
    deleteSchedule: unsupported('deleteSchedule'),
    listAttendanceCategories: unsupported('listAttendanceCategories'),
    createAttendanceCategory: unsupported('createAttendanceCategory'),
    renameAttendanceCategory: unsupported('renameAttendanceCategory'),
    disableAttendanceCategory: unsupported('disableAttendanceCategory'),
    signAttendance: unsupported('signAttendance'),
    getResourcesRoot: unsupported('getResourcesRoot'),
    createResourceFolder: unsupported('createResourceFolder'),
    createSong: unsupported('createSong'),
    listSongs: unsupported('listSongs'),
    getSongDetail: unsupported('getSongDetail'),
    createSongRelatedFolder: unsupported('createSongRelatedFolder'),
    createResourceUploadIntent: unsupported('createResourceUploadIntent'),
    uploadResourceFile: unsupported('uploadResourceFile'),
    attachOrdinaryResourceFile: unsupported('attachOrdinaryResourceFile'),
    attachSongRelatedResourceFile: unsupported('attachSongRelatedResourceFile'),
    updateSongCurrentScore: unsupported('updateSongCurrentScore'),
    getResourceFileAccess: unsupported('getResourceFileAccess'),
    readMarkdownResourceFile: unsupported('readMarkdownResourceFile'),
    listNotifications: unsupported('listNotifications'),
    getUnreadNotificationState: unsupported('getUnreadNotificationState'),
    markNotificationRead: unsupported('markNotificationRead'),
    listAssignments: unsupported('listAssignments'),
    createAssignment: unsupported('createAssignment'),
    getAssignmentDetail: unsupported('getAssignmentDetail'),
    replaceAssignmentDeliveryRules: unsupported('replaceAssignmentDeliveryRules'),
    createUploadIntent: unsupported('createUploadIntent'),
    uploadAssignmentFile: unsupported('uploadAssignmentFile'),
    completeUpload: unsupported('completeUpload'),
    submitMyAssignment: unsupported('submitMyAssignment'),
    getAssignmentSubmissionAccess: unsupported('getAssignmentSubmissionAccess')
  };
}

export function createBrowserCloudAuthTokenStore(key: string): CloudAuthSliceTokenStore {
  return {
    get: () => localStorage.getItem(key),
    set: (token) => localStorage.setItem(key, token),
    clear: () => localStorage.removeItem(key)
  };
}

function createEmptyAuthSliceViewModel(currentUser: UserAccount | null): MemberManagementViewModel {
  return {
    currentUser,
    currentChoir: null,
    currentMembership: null,
    currentSeason: null,
    isCurrentUserAdmin: false,
    currentPermissionKeys: [],
    joinedChoirs: [],
    memberGroups: [],
    memberGroupSummaries: [],
    members: [],
    availableInvitationCodes: [],
    pendingJoinRequests: [],
    ownJoinRequests: [],
    schedules: [],
    attendanceCategories: []
  };
}

function unsupported(method: string): () => Promise<never> {
  return async () => {
    if (
      method === 'listSchedules' ||
      method === 'listUpcomingSchedules' ||
      method === 'getScheduleDetail' ||
      method === 'getHomeUpcomingSchedule' ||
      method === 'getScheduleEditOptions' ||
      method === 'createSchedule' ||
      method === 'updateSchedule' ||
      method === 'deleteSchedule' ||
      method === 'listAttendanceCategories' ||
      method === 'createAttendanceCategory' ||
      method === 'renameAttendanceCategory' ||
      method === 'disableAttendanceCategory' ||
      method === 'signAttendance'
    ) {
      throw new Error('Schedule service is not implemented for cloud auth slice');
    }

    if (
      method === 'getResourcesRoot' ||
      method === 'createResourceFolder' ||
      method === 'createSong' ||
      method === 'listSongs' ||
      method === 'getSongDetail' ||
      method === 'createSongRelatedFolder' ||
      method === 'createResourceUploadIntent' ||
      method === 'uploadResourceFile' ||
      method === 'attachOrdinaryResourceFile' ||
      method === 'attachSongRelatedResourceFile' ||
      method === 'updateSongCurrentScore' ||
      method === 'getResourceFileAccess' ||
      method === 'readMarkdownResourceFile' ||
      method === 'listNotifications' ||
      method === 'getUnreadNotificationState' ||
      method === 'markNotificationRead'
    ) {
      throw new Error('Resource service is not implemented for cloud auth slice');
    }

    if (
      method === 'listAssignments' ||
      method === 'createAssignment' ||
      method === 'getAssignmentDetail' ||
      method === 'replaceAssignmentDeliveryRules' ||
      method === 'createUploadIntent' ||
      method === 'uploadAssignmentFile' ||
      method === 'completeUpload' ||
      method === 'submitMyAssignment' ||
      method === 'getAssignmentSubmissionAccess'
    ) {
      throw new Error('Assignment service is not implemented for cloud auth slice');
    }

    throw new Error(CLOUD_AUTH_SLICE_UNSUPPORTED_OPERATION_MESSAGE);
  };
}
